import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { UserPlus, GripVertical, Pencil, Trash2, Check, X, Download, Users } from 'lucide-react';

// Sortable player item component using shadcn styling
function SortablePlayerItem({ player, onDeletePlayer, onUpdatePlayerName }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(player.name);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: player.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDeletePlayer = (playerId, playerName) => {
    if (window.confirm(`Are you sure you want to delete ${playerName}? This action cannot be undone.`)) {
      onDeletePlayer(playerId);
    }
  };

  const handleSave = () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === player.name) {
      setIsEditing(false);
      setEditName(player.name);
      return;
    }
    onUpdatePlayerName(player.id, trimmed);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditName(player.name);
    setIsEditing(false);
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="flex justify-between items-center bg-card border rounded-lg p-3 shadow-sm hover:shadow transition-shadow"
    >
      <div className="flex items-center gap-2 flex-1">
        <div
          className={`cursor-move p-1 text-muted-foreground hover:text-foreground ${isEditing ? 'opacity-30 pointer-events-none' : ''}`}
          {...(!isEditing ? listeners : {})}
        >
          <GripVertical className="w-4 h-4" />
        </div>
        {isEditing ? (
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="h-8 flex-1 text-sm"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
          />
        ) : (
          <span className="truncate mr-2 font-medium text-sm">{player.name}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {isEditing ? (
          <>
            <Button size="sm" variant="success" className="h-7 px-2 text-xs flex items-center gap-1" onClick={handleSave}>
              <Check className="w-3.5 h-3.5" />
              Save
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs flex items-center gap-1" onClick={handleCancel}>
              <X className="w-3.5 h-3.5" />
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs flex items-center gap-1" onClick={() => setIsEditing(true)}>
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7 hover:bg-destructive/10 border-input"
              title="Delete player"
              onClick={() => handleDeletePlayer(player.id, player.name)}
            >
              <Trash2 className="w-3.5 h-3.5 text-red-500 hover:text-red-600" />
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

function PlayerRoster({
  players,
  onAddPlayer,
  onDeletePlayer,
  onReorderPlayers,
  onUpdatePlayerName,
  seasonName,
  seasons = [],
  selectedSeasonId,
  onImportRoster
}) {
  const [newPlayerName, setNewPlayerName] = useState('');
  const [sourceSeasonId, setSourceSeasonId] = useState('');
  const [isImportOpen, setIsImportOpen] = useState(false);

  const availableSeasonsToImport = seasons.filter(s => s.id !== selectedSeasonId);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newPlayerName.trim()) {
      onAddPlayer(newPlayerName);
      setNewPlayerName('');
    }
  };

  const handleImportSubmit = (e) => {
    e.preventDefault();
    if (sourceSeasonId && onImportRoster) {
      onImportRoster(sourceSeasonId);
      setIsImportOpen(false);
      setSourceSeasonId('');
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = players.findIndex(player => player.id === active.id);
      const newIndex = players.findIndex(player => player.id === over.id);
      
      const newPlayers = arrayMove(players, oldIndex, newIndex);
      onReorderPlayers(newPlayers);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      {/* Import Roster Banner/Button if other seasons exist */}
      {availableSeasonsToImport.length > 0 && selectedSeasonId && (
        <div className="p-3 bg-muted/40 rounded-lg border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Download className="w-4 h-4 text-primary shrink-0" />
            <span>Need players from a previous season? Import them automatically.</span>
          </div>

          {!isImportOpen ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsImportOpen(true)}
              className="h-8 text-xs shrink-0 flex items-center gap-1.5"
            >
              <Users className="w-3.5 h-3.5" />
              Import Roster from Season
            </Button>
          ) : (
            <form onSubmit={handleImportSubmit} className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <select
                value={sourceSeasonId}
                onChange={(e) => setSourceSeasonId(e.target.value)}
                className="flex h-8 rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-medium"
              >
                <option value="">Select Season to Copy Roster...</option>
                {availableSeasonsToImport.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>

              <Button type="submit" size="sm" disabled={!sourceSeasonId} className="h-8 text-xs">
                Import Now
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setIsImportOpen(false); setSourceSeasonId(''); }}
                className="h-8 text-xs px-2"
              >
                Cancel
              </Button>
            </form>
          )}
        </div>
      )}

      {/* Add Player Form */}
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        <Input
          type="text"
          value={newPlayerName}
          onChange={(e) => setNewPlayerName(e.target.value)}
          placeholder={seasonName && seasonName !== 'All Seasons' ? `Enter player name for ${seasonName}` : "Enter player name"}
          className="flex-1"
        />
        <Button type="submit" className="flex items-center gap-1.5 justify-center">
          <UserPlus className="w-4 h-4" />
          Add Player
        </Button>
      </form>
      
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        💡 Drag player handle to reorder roster positions.
      </div>
      
      {players.length === 0 ? (
        <div className="p-4 text-center text-sm text-muted-foreground border rounded-lg bg-muted/20">
          No players in this roster yet. Add a player above or import from a previous season!
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={players.map(player => player.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {players.map(player => (
                <SortablePlayerItem
                  key={player.id}
                  player={player}
                  onDeletePlayer={onDeletePlayer}
                  onUpdatePlayerName={onUpdatePlayerName}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

export default PlayerRoster;
